/**
 * Keyboard Shortcut Hook
 *
 * Provides a clean API for registering keyboard shortcuts in components.
 * Handles cleanup, modifier keys, and edge cases automatically.
 *
 * @example
 * // Plain Escape — omitted modifiers must be OFF, so this will NOT fire on
 * // Shift+Escape, Cmd+Escape, etc.
 * useKeyboardShortcut('Escape', () => closeModal())
 *
 * @example
 * // With modifiers — listed modifier must be ON, omitted modifiers must be OFF.
 * // `{ shift: true }` matches Shift+E only (not Shift+Cmd+E).
 * useKeyboardShortcut('E', () => openEdit(), { shift: true })
 * useKeyboardShortcut('Enter', () => submitForm(), { meta: true })
 *
 * @example
 * // Conditional enable
 * useKeyboardShortcut('Escape', () => close(), { enabled: isOpen })
 */

import { useEffect, useRef } from 'react'

type ModifierKeys = {
  shift?: boolean
  meta?: boolean // Cmd on Mac, Win on Windows
  ctrl?: boolean
  alt?: boolean
}

export type Options = ModifierKeys & {
  enabled?: boolean // Allow conditionally enabling (default: true)
  preventDefault?: boolean // Prevent default browser behavior (default: true)
  stopPropagation?: boolean // Stop event bubbling to ancestors (default: false)
  // Stop other listeners on the SAME node from firing (default: false).
  // Necessary for nested-modal Escape scoping (#6280), where both modals
  // register `keydown` on `document` — `stopPropagation` does not gate
  // sibling listeners on the same node, but `stopImmediatePropagation` does.
  stopImmediatePropagation?: boolean
  ignoreInInput?: boolean // Ignore shortcut when typing in input/textarea (default: false)
}

/**
 * Per-shortcut overrides for {@link useKeyboardShortcuts} (#13205).
 *
 * The hook-level flags apply to the whole map, which is wrong for any
 * shortcut whose handler is CONDITIONALLY a no-op: cancelling the default of
 * an activation the handler will not perform leaves the key doing nothing at
 * all. `Enter` in `useListKeyboardNavigation` is the measured case — with no
 * list item selected, `Enter` on a Tab-focused card link was cancelled by the
 * document-level listener and never reached the anchor.
 *
 * `preventDefault` accepts a PREDICATE as well as a boolean, because some of
 * those conditions are only knowable at event time. Whether the list hook
 * will open its selection depends on what is focused when `Enter` arrives —
 * a render-time boolean cannot express that, and one that tried would cancel
 * `Enter` on a focused button whenever a list selection happened to exist.
 *
 * Purely additive: an absent entry (and an absent `shortcutOptions` object,
 * which is what every other caller passes) leaves behaviour unchanged.
 */
export type PerShortcutOptions = {
  preventDefault?: boolean | ((event: KeyboardEvent) => boolean)
}

/**
 * Register a keyboard shortcut
 *
 * @param key - The key to listen for (e.g., 'Escape', 'Enter', 'E')
 * @param callback - Function to call when shortcut is triggered
 * @param options - Modifier keys and behavior options
 */
export function useKeyboardShortcut(
  key: string,
  callback: () => void,
  options: Options = {},
) {
  const {
    shift = false,
    meta = false,
    ctrl = false,
    alt = false,
    enabled = true,
    preventDefault = true,
    stopPropagation = false,
    stopImmediatePropagation = false,
    ignoreInInput = false,
  } = options

  // Use ref to avoid recreating listener when callback changes
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    if (!enabled) return // Don't add listener if disabled

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if we're in an input field
      if (ignoreInInput) {
        const target = e.target as HTMLElement
        const inInput =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable

        if (inInput) return
      }

      // Check key match (case-sensitive for letters)
      if (e.key !== key) return

      // Check modifiers match EXACTLY.
      //
      // Each of `shift`, `meta`, `ctrl`, `alt` defaults to `false` in the
      // destructure above, so omitting a modifier from the options is
      // equivalent to passing `false`:
      //   - If the modifier is `true`,  the corresponding event flag must be true.
      //   - If the modifier is `false` (the default when omitted), the event
      //     flag must be false.
      //
      // There is no "don't care about this modifier" option here. Callers
      // wanting "match regardless of modifier state" should branch on the
      // event's modifier flags inside their own handler — see
      // `isFormSubmitShortcut` in `src/lib/keyboard-utils.ts` for an example
      // (`e.metaKey || e.ctrlKey` covers the cross-platform Cmd-or-Ctrl case).
      const shiftMatch = e.shiftKey === shift
      const metaMatch = e.metaKey === meta
      const ctrlMatch = e.ctrlKey === ctrl
      const altMatch = e.altKey === alt

      if (!shiftMatch || !metaMatch || !ctrlMatch || !altMatch) return

      // All checks passed - trigger callback
      if (preventDefault) e.preventDefault()
      if (stopPropagation) e.stopPropagation()
      if (stopImmediatePropagation) e.stopImmediatePropagation()

      callbackRef.current()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [
    key,
    shift,
    meta,
    ctrl,
    alt,
    enabled,
    preventDefault,
    stopPropagation,
    stopImmediatePropagation,
    ignoreInInput,
  ])
}

/**
 * Register multiple keyboard shortcuts at once
 * Useful for components with many shortcuts
 *
 * @example
 * useKeyboardShortcuts({
 *   'Shift+U': () => openUpdate(),
 *   'Shift+E': () => openEdit(),
 *   'Shift+H': () => openHistory(),
 * })
 *
 * @example
 * // Per-shortcut override (#13205): only cancel Enter's default when this
 * // hook's handler is actually going to act on it. A predicate is evaluated
 * // against the event, so the decision can read `event.target`.
 * useKeyboardShortcuts(
 *   { Enter: selectItem, j: moveDown },
 *   { shortcutOptions: { Enter: { preventDefault: canSelect } } },
 * )
 */
export function useKeyboardShortcuts<K extends string>(
  // `Partial` because callers build the map with conditional spreads
  // (`...(canEdit && { 'Shift+E': onEdit })`), which types the key as
  // optional. Requiring every inferred key would reject those callers.
  shortcuts: Partial<Record<K, (event: KeyboardEvent) => void>>,
  options: Omit<Options, keyof ModifierKeys> & {
    /**
     * Overrides keyed by the SAME combo string used in `shortcuts`
     * (`'Shift+E'`, not `'E'`). `Partial<Record<NoInfer<K>, …>>` binds the
     * keys to the shortcut map's own, so a typo (`enter`, `'shift+E'`) is a
     * compile error rather than an override that silently never fires.
     * `NoInfer` is load-bearing: without it this position is an inference
     * site too, and a misspelled key WIDENS `K` to include it instead of
     * being rejected. Re-read on every event, so a value derived from state
     * stays correct after a re-render.
     */
    shortcutOptions?: Partial<Record<NoInfer<K>, PerShortcutOptions>>
  } = {},
) {
  const {
    enabled = true,
    preventDefault = true,
    stopPropagation = false,
    stopImmediatePropagation = false,
    ignoreInInput = false,
    shortcutOptions,
  } = options

  // Use ref to avoid recreating listener when shortcuts change
  const shortcutsRef = useRef(shortcuts)

  useEffect(() => {
    shortcutsRef.current = shortcuts
  }, [shortcuts])

  // Same treatment for the per-shortcut overrides: they are typically derived
  // from render state (`{ preventDefault: selectedIndex !== null }`), so the
  // listener has to read the latest object rather than close over the one it
  // was mounted with.
  const shortcutOptionsRef = useRef(shortcutOptions)

  useEffect(() => {
    shortcutOptionsRef.current = shortcutOptions
  }, [shortcutOptions])

  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if we're in an input field
      if (ignoreInInput) {
        const target = e.target as HTMLElement
        const inInput =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable

        if (inInput) return
      }

      // Parse each shortcut combo and check for match
      for (const [combo, callback] of Object.entries(
        shortcutsRef.current as Record<
          string,
          ((event: KeyboardEvent) => void) | undefined
        >,
      )) {
        // A conditional spread can leave a key present with an undefined
        // value; that is "not registered", not "registered as a no-op".
        if (callback && matchesShortcut(e, combo)) {
          // A per-combo override wins over the hook-level flag; `undefined`
          // (no entry, or no `shortcutOptions` at all) falls through to it. A
          // predicate is evaluated against THIS event, which is what lets a
          // caller decide from `event.target` rather than from render state.
          const override = (
            shortcutOptionsRef.current as
              | Record<string, PerShortcutOptions>
              | undefined
          )?.[combo]?.preventDefault
          const shouldPreventDefault =
            typeof override === 'function' ? override(e) : override
          if (shouldPreventDefault ?? preventDefault) e.preventDefault()
          if (stopPropagation) e.stopPropagation()
          if (stopImmediatePropagation) e.stopImmediatePropagation()
          // The event is passed so a handler can make the SAME event-time
          // decision the `preventDefault` predicate above made. Widening the
          // parameter is source-compatible: every existing `() => void`
          // handler stays assignable and simply ignores it.
          callback(e)
          break // Only trigger first matching shortcut
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [
    enabled,
    preventDefault,
    stopPropagation,
    stopImmediatePropagation,
    ignoreInInput,
  ])
}

/**
 * Check if a keyboard event matches a shortcut combo string
 *
 * @param event - The keyboard event
 * @param combo - Shortcut string like "Shift+E", "Cmd+Enter", "Escape"
 */
function matchesShortcut(event: KeyboardEvent, combo: string): boolean {
  const parts = combo.split('+').map(p => p.trim())
  const key = parts[parts.length - 1] // Last part is the key
  const modifiers = parts.slice(0, -1) // Everything before is modifiers

  // Check key match
  if (event.key !== key) return false

  // Check modifiers
  const hasShift = modifiers.includes('Shift')
  const hasMeta = modifiers.includes('Cmd') || modifiers.includes('Meta')
  const hasCtrl = modifiers.includes('Ctrl')
  const hasAlt = modifiers.includes('Alt') || modifiers.includes('Option')

  // Modifiers must match exactly
  if (event.shiftKey !== hasShift) return false
  if (event.metaKey !== hasMeta) return false
  if (event.ctrlKey !== hasCtrl) return false
  if (event.altKey !== hasAlt) return false

  return true
}
